import * as React from 'react';
import { useEffect, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { uuid } from '../helpers';
import { useZakeke } from '../hooks/useZakeke';
import { useInternalState } from '../state/provider';

const ZakekeDesignerContainer = styled.div`
	width: 100%;
	height: 100%;
	position: relative;
	outline: none !important;

	canvas {
		outline: none !important;
	}
`;

interface ExposedMethods {
	zoomIn: () => void;
	zoomOut: () => void;
}

// Persistent mode will keep the SceneEditor alive after unmount.
// You should save the current editor with the ref
export const ZakekeDesigner = React.forwardRef<ExposedMethods, { areaId: number }>((props, ref) => {
	const internalState = useInternalState();
	const stateRef = useRef(internalState);
	stateRef.current = internalState;
	const store = stateRef.current.state.environment;

	const { designUpdate } = useZakeke();
	const [guid] = React.useState(uuid());

	useEffect(() => {
		const container = document.getElementById('zakeke-designer-container-' + guid)!;
		const newCustomizer = new MPlaza.Customizer(false, false, null, true);
		newCustomizer.showSideImage = false;
		newCustomizer.addItemChangedListener(handleItemChanged);
		newCustomizer.addItemRemovedListener(handleItemRemoved);

		// Hide DPI warnings
		newCustomizer.setCustomizedMessages([
			{
				Description: '',
				EventID: MPlaza.EventMessages.DPIWarning,
				Visible: false
			}
		]);

		newCustomizer.appendTo(container);
		internalState.dispatch({
			type: 'CUSTOMIZER',
			customizer: newCustomizer
		});

		return () => {
			internalState.dispatch({
				type: 'CUSTOMIZER',
				customizer: null
			});
		};
	}, []);

	useEffect(() => {
		if (store.internalProduct) {
			const customizer = stateRef?.current?.state?.customizer;
			const side = store.internalProduct
				.get('colors')
				.at(0)
				.get('sides')
				.find((x) => x.get('sideID') == props.areaId);

			if (side && customizer && store.internalCurrentTemplate) {
				customizer.model = store.internalProduct;
				customizer.setDesign(store.internalCurrentTemplate);
				customizer.setSide(side).then(() => {
					customizer.adaptZoomToSideAreas(false);
				});
			}
		}
	}, [props.areaId, store.currentTemplate, !!stateRef?.current?.state?.customizer]);

	const handleItemChanged = () => {
		designUpdate();
	};

	const handleItemRemoved = () => {
		designUpdate();
	};

	const zoomIn = () => {
		const customizer = stateRef?.current?.state?.customizer;
		if (customizer) customizer.zoomIn();
	};

	const zoomOut = () => {
		const customizer = stateRef?.current?.state?.customizer;
		if (customizer) customizer.zoomOut();
	};

	// Expose the zoom methods to ref
	useImperativeHandle(
		ref,
		() => ({
			zoomIn,
			zoomOut
		}),
		[]
	);

	return <ZakekeDesignerContainer id={'zakeke-designer-container-' + guid}></ZakekeDesignerContainer>;
});
